import { useCallback, useEffect, useState } from 'react'
import { useDeskBridge } from '../hooks/useDeskBridge'
import { requestDraft, requestSummary, type Tone } from '../api/draft'
import { getOrgSettings, type OrgTone } from '../api/orgSettings'
import { AppHeader } from '../components/AppHeader'
import { TicketCard } from '../components/TicketCard'
import { DraftEditor } from '../components/DraftEditor'
import { SourceList } from '../components/SourceList'
import { SummaryCard } from '../components/SummaryCard'
import { ConversationView } from '../components/ConversationView'
import { BridgeLog } from '../components/BridgeLog'
import type { DraftResponse, MeResponse, SummaryResponse } from '../types'
import { Linked } from '../components/Linked'

/**
 * Who has already been sent to settings over a wrong portal.
 *
 * Module level so walking back to the ticket does not bounce them again, and
 * keyed on the agent so the next person to sign in on this tab still gets
 * taken there.
 */
let sentToSettingsFor = ''

/** Shapes an agent can ask for, in the order they get asked for. */
const TONES: Array<[Tone, string]> = [
  ['short', 'Short'],
  ['friendly', 'Friendly'],
  ['formal', 'Formal'],
  ['details', 'Ask for details'],
]

const PILL: Record<string, string> = {
  waiting: 'Waiting for host…',
  connected: 'Connected to Desk',
  standalone: 'Standalone (no host)',
  // Named so it is never mistaken for the real integration.
  url: 'Test ticket from URL',
}

export function PanelView({
  me,
  onOpenSettings,
  onAuthLost,
}: {
  me: MeResponse | null
  onOpenSettings: () => void
  onAuthLost: () => void
}) {
  const { status, ticket, log, requestTicket } = useDeskBridge()

  // The help desk this widget is open in, against the one the connection was
  // granted for. Nothing here can work when they differ, and a reload drops
  // the agent on this screen rather than on settings — so it has to say so
  // here too.
  const connectedOrg = me?.zoho.orgId ? String(me.zoho.orgId) : ''
  const wrongPortal = Boolean(
    me?.zoho.valid && ticket?.orgId && connectedOrg && String(ticket.orgId) !== connectedOrg,
  )

  // Nothing on this screen can work, so go where the fix is rather than
  // explaining it here. Once only: an agent who walks back to the ticket is
  // allowed to stay there.
  const uid = me?.uid ?? ''
  useEffect(() => {
    if (wrongPortal && uid && sentToSettingsFor !== uid) {
      sentToSettingsFor = uid
      onOpenSettings()
    }
  }, [wrongPortal, uid, onOpenSettings])

  const [draft, setDraft] = useState<DraftResponse | null>(null)
  const [reply, setReply] = useState('')
  const [summary, setSummary] = useState<SummaryResponse | null>(null)
  // Two flags rather than one: summarising must not disable the draft button,
  // since reading the ticket and answering it are separate things an agent may
  // want in either order.
  const [loading, setLoading] = useState(false)
  const [summarising, setSummarising] = useState(false)
  const [tone, setTone] = useState<Tone>('')
  const [ask, setAsk] = useState('')

  // The org's own tones sit beside the built-in four. Fetched once: a team's
  // list changes about as often as its house style does.
  const [orgTones, setOrgTones] = useState<OrgTone[]>([])

  useEffect(() => {
    void getOrgSettings()
      .then((s) => setOrgTones(s.tones ?? []))
      // A panel without the custom chips is still a working panel.
      .catch(() => setOrgTones([]))
  }, [])
  // One slot per action, shown under the button that failed. A single slot
  // at the top of the panel put every error above the summary button, so a
  // draft that failed looked like the summary had.
  const [summaryError, setSummaryError] = useState('')
  const [draftError, setDraftError] = useState('')

  const onDraft = useCallback(async () => {
    if (!ticket?.id) return
    setLoading(true)
    setDraftError('')
    try {
      const res = await requestDraft(ticket.id, ticket.subject, tone, ask)
      setDraft(res)
      setReply(res.reply)
      // Used, so it goes. Left in the box it would quietly steer the next
      // ticket the agent opens.
      setAsk('')
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setDraftError(msg)
      // A rejected credential or expired session sends us back through the gate
      // rather than leaving a dead button behind.
      if (/sign in|session|reconnect|settings/i.test(msg)) onAuthLost()
    } finally {
      setLoading(false)
    }
    // tone and ask belong here: without them the callback keeps the values from
    // the render that created it, which were the empty ones, and every draft
    // went out as though neither had been touched.
  }, [ticket, tone, ask, onAuthLost])

  const onSummarise = useCallback(async () => {
    if (!ticket?.id) return
    setSummarising(true)
    setSummaryError('')
    try {
      setSummary(await requestSummary(ticket.id))
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setSummaryError(msg)
      if (/sign in|session|reconnect|settings/i.test(msg)) onAuthLost()
    } finally {
      setSummarising(false)
    }
  }, [ticket, onAuthLost])

  return (
    <>
      <AppHeader
        me={me}
        status={status}
        statusLabel={PILL[status]}
        onAvatar={onOpenSettings}
      />

      <TicketCard ticket={ticket} />

      {/* Read the ticket, then answer it. Each button sits directly above what
          it produces, in the order the work is actually done. */}
      <button className="secondary" onClick={onSummarise} disabled={!ticket?.id || summarising}>
        {summarising ? 'Summarising…' : 'Summarise conversation'}
      </button>
      {summaryError && <p className="inline-error"><Linked text={summaryError} /></p>}

      {summary && <SummaryCard summary={summary} />}

      {/* Optional, and empty on most drafts. Cleared once used: an
          instruction left over from the last ticket would apply itself to the
          next one, which is the kind of mistake noticed after it has reached a
          customer. */}
      <label className="field">
        <span>Anything to add?</span>
        <textarea
          className="ask"
          rows={2}
          value={ask}
          disabled={loading}
          placeholder="Optional, e.g. refund approved, apologise for the delay"
          onChange={(e) => setAsk(e.target.value)}
        />
      </label>

      {/* Shape for this one reply, chosen where the ticket is — not a setting.
          The org's own wording still governs every other draft. */}
      <div className="tones" role="group" aria-label="Reply tone">
        {[...TONES, ...orgTones.map((t) => [t.id, t.label] as [Tone, string])].map(
          ([id, label]) => (
            <button
              key={id}
              className={tone === id ? 'tone on' : 'tone'}
              aria-pressed={tone === id}
              disabled={loading}
              onClick={() => setTone(tone === id ? '' : id)}
            >
              {label}
            </button>
          ),
        )}
      </div>

      <button onClick={onDraft} disabled={!ticket?.id || loading}>
        {loading ? 'Generating…' : 'Generate reply'}
      </button>
      {draftError && <p className="inline-error"><Linked text={draftError} /></p>}

      {draft && (
        <>
          <DraftEditor
            value={reply}
            onChange={setReply}
            unread={draft.unreadAttachments}
            model={draft.model}
            ticketId={ticket?.id}
            embedded={status === 'connected'}
          />
          <ConversationView messages={draft.conversation ?? []} />
          <SourceList sources={draft.sources} />
        </>
      )}

      {/* The log and the manual fetch are how a broken connection gets
          diagnosed. Dev builds only: an agent has no use for a bridge log, and
          a reviewer reading one sees debugging left switched on. */}
      {import.meta.env.DEV && (
        <details className="diagnostics">
          <summary>Connection details</summary>

          <p className="sub">{me?.email}</p>

          <button className="secondary" onClick={requestTicket}>
            Request ticket data
          </button>

          <BridgeLog lines={log} />
        </details>
      )}
    </>
  )
}
